import { useEffect, useState, useRef } from "react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { motion, AnimatePresence } from "framer-motion";
import { getEnvironment } from "@packages/utils";
import {
  QuestionHelperResponseDto,
  StepContent,
} from "@packages/api/madlenAPI.schemas";
import { Skeleton } from "@core/skeleton";
import LoadingDot from "@core/loading";

const parseTextContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      // Italic text (double stars)
      return (
        <em key={index} className="italic text-gray-800 dark:text-gray-200">
          {part.slice(2, -2)}
        </em>
      );
    } else if (part.startsWith("*") && part.endsWith("*")) {
      // Bold text (single star)
      return (
        <strong
          key={index}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          {part.slice(1, -1)}
        </strong>
      );
    }
    return part;
  });
};

const QuestionSolver = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const direction = useRef<"next" | "prev">("next");
  const token = new URLSearchParams(window.location.search).get("token");
  const id = new URLSearchParams(window.location.search).get("id");
  const [questionData, setQuestionData] =
    useState<QuestionHelperResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for swipe detection
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < (questionData?.solutionSteps?.length ?? 0) - 1) {
      direction.current = "next";
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      direction.current = "prev";
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Swipe detection
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (distance > threshold) {
      // Swipe left - next step
      goToNextStep();
    } else if (distance < -threshold) {
      // Swipe right - previous step
      goToPrevStep();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        goToPrevStep();
      } else if (event.key === "ArrowRight") {
        goToNextStep();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentStep, questionData?.solutionSteps?.length]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setError(null);

      const baseUrl =
        getEnvironment() === "DEV"
          ? "https://api-dev.madlen.io/api"
          : "https://api.madlen.io/api";

      fetch(`${baseUrl}/question-helper/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          UserRole: "STUDENT",
          OrganizationId: "1",
        },
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          setQuestionData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching question data:", err);
          setError("Soru yüklenirken bir hata oluştu. Lütfen tekrar deneyin.");
          setIsLoading(false);
        });
    } else {
      setError("Geçersiz soru ID'si");
      setIsLoading(false);
    }
  }, [id, token]);

  const renderContent = (content: StepContent) => {
    switch (content.type) {
      case "TEXT":
        return (
          <span className="text-gray-700 dark:text-gray-300">
            {parseTextContent(content.content ?? "")}
          </span>
        );
      case "LATEX_INLINE":
        return (
          <span className="inline-block">
            <InlineMath>{content.content}</InlineMath>
          </span>
        );
      case "LATEX_BLOCK":
        return (
          <div className="my-3 math-container">
            <BlockMath>{content.content}</BlockMath>
          </div>
        );
      case "LIST_ITEM":
        return (
          <div className="flex items-start gap-2 ml-4 my-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {parseTextContent(content.content ?? "")}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  // Loading screen with beautiful animations
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen max-w-full overflow-x-hidden">
        {/* Header skeleton */}
        <div className="p-4">
          <div className="pl-2 border-l-4 border-blue-200 bg-gradient-to-r from-blue-50">
            <div className="py-2 mb-2 flex items-center gap-2">
              <Skeleton className="min-w-6 min-h-6 rounded-full" />
              <Skeleton className="h-6 w-48" />
            </div>
          </div>
        </div>

        {/* Content skeleton */}
        <div className="flex-1 p-4 space-y-4">
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
            <div className="my-4">
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        </div>

        {/* Navigation skeleton */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <Skeleton className="w-12 h-12 rounded-full" />
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-16" />
              <div className="flex space-x-1">
                {Array.from({ length: 3 }, (_, index) => (
                  <Skeleton key={index} className="w-2 h-2 rounded-full" />
                ))}
              </div>
            </div>
            <Skeleton className="w-12 h-12 rounded-full" />
          </div>
        </div>

        {/* Overlay with enhanced loading animation */}
        <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
          <LoadingDot
            title="Soru çözümü hazırlanıyor..."
            themeColor="blue"
            fullHeight={false}
          />
        </div>
      </div>
    );
  }

  // Error screen
  if (error) {
    return (
      <div className="flex flex-col h-screen max-w-full overflow-x-hidden items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Bir Hata Oluştu
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Data validation
  if (
    !questionData ||
    !questionData.solutionSteps ||
    !questionData.solutionSteps.length ||
    !questionData.solutionSteps[0].content
  ) {
    return (
      <div className="flex flex-col h-screen max-w-full overflow-x-hidden items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-yellow-600 dark:text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Soru Bulunamadı
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Bu soru için çözüm adımları bulunamadı.
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalSteps = questionData?.solutionSteps?.length ?? 0;
  const currentStepData = questionData?.solutionSteps?.[currentStep];

  return (
    <div
      className="flex flex-col h-screen max-w-full overflow-x-hidden"
      style={{ touchAction: "pan-y" }}
    >
      {/* Main content area */}
      <div
        ref={containerRef}
        className="flex-1 p-4 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{
              opacity: 0,
              x: direction.current === "next" ? 30 : -30,
            }}
            animate={{ opacity: 1, x: 0 }}
            exit={{
              opacity: 0,
              x: direction.current === "next" ? -30 : 30,
            }}
            transition={{ duration: 0.3 }}
            className="pb-4 to-white dark:from-blue-900/30 dark:to-gray-900/10 max-w-full overflow-x-hidden"
          >
            <div className="pl-2 border-l-4 border-blue-500 bg-gradient-to-r from-blue-50">
              <h2 className="text-base py-2 font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                <span className="flex items-center justify-center min-w-6 min-h-6 rounded-full bg-blue-500 text-white text-sm font-bold">
                  {currentStep + 1}
                </span>
                {currentStepData?.title}
              </h2>
            </div>
            <div className="leading-relaxed space-y-1 max-w-full overflow-x-hidden">
              {currentStepData?.content?.map((content, idx) =>
                content.type === "LATEX_INLINE" || content.type === "TEXT" ? (
                  <span key={idx} className="overflow-x-hidden">
                    {renderContent(content)}
                  </span>
                ) : (
                  <div key={idx} className={`max-w-full overflow-x-hidden`}>
                    {renderContent(content)}
                  </div>
                ),
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation controls */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {/* Previous button */}
          <button
            onClick={goToPrevStep}
            disabled={currentStep === 0}
            className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
              currentStep === 0
                ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl active:scale-95"
            }`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Step indicator */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {currentStep + 1} / {totalSteps}
            </span>
            <div className="flex space-x-1">
              {Array.from({ length: totalSteps }, (_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    index === currentStep
                      ? "bg-blue-500"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={goToNextStep}
            disabled={currentStep === totalSteps - 1}
            className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
              currentStep === totalSteps - 1
                ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl active:scale-95"
            }`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionSolver;
